import L from "leaflet";
import { Marker } from "react-leaflet";
import { VehicleGeolocated } from "./index";
import { useMemo, useRef } from "react";
import vehicleDotIconUrl from "@qivia/ui/src/assets/vehicleDot.svg";
import { VehicleLabelTooltip } from "./vehicleLabelTooltip";
import { createCarIcon } from "./carIcon";
import { isTripOngoing } from "@qivia/ui";
import { Trips } from "../fleet/trips/tripsApi";

export const VehiclesShowOnMap = (props: {
  handleOpenVehicleTrip: (vehicle: VehicleGeolocated) => void;
  hoveredVehicleId: string | null;
  showTripForVehicleUuid: string | null;
  vehiclesToDisplay: VehicleGeolocated[];
  tripsByDayList: Trips[];
  date: Date;
}) => {
  const markersRef = useRef(new Map<string, L.Marker>());
  const {
    handleOpenVehicleTrip,
    hoveredVehicleId,
    showTripForVehicleUuid,
    vehiclesToDisplay,
    tripsByDayList,
    date,
  } = props;

  const vehiclesToShowOnMap = useMemo(
    () =>
      vehiclesToDisplay.filter((v) => {
        if (
          v.uuid === showTripForVehicleUuid &&
          v.dataPoints &&
          v.dataPoints.length > 0
        )
          return false;
        if (hoveredVehicleId && v.uuid !== hoveredVehicleId) return false;
        return true;
      }),
    [hoveredVehicleId, showTripForVehicleUuid, vehiclesToDisplay],
  );

  const ongoingVehicleUuids = useMemo(() => {
    if (date.toDateString() !== new Date().toDateString())
      return new Set<string>();
    return new Set(
      vehiclesToDisplay
        .filter((v) =>
          isTripOngoing(
            v.lastDate ?? undefined,
            tripsByDayList
              .filter((t) => t.vehicleUuid === v.uuid)
              .flatMap((t) => t.trips),
          ),
        )
        .map((v) => v.uuid),
    );
  }, [date, vehiclesToDisplay, tripsByDayList]);

  return (
    <>
      {vehiclesToShowOnMap.map((v) => (
        <Marker
          position={[v.lastLatitude || 0, v.lastLongitude || 0]}
          key={v.uuid}
          ref={(marker) => {
            if (marker) markersRef.current.set(v.uuid, marker);
          }}
          icon={
            ongoingVehicleUuids.has(v.uuid) ? createCarIcon(0) : vehicleOnIcon
          }
          eventHandlers={{
            click: () => handleOpenVehicleTrip(v),
            mouseover: () =>
              markersRef.current.get(v.uuid)?.getTooltip()?.bringToFront(),
          }}
        >
          <VehicleLabelTooltip vehicle={v} />
        </Marker>
      ))}
    </>
  );
};

const vehicleOnIcon = L.icon({
  iconUrl: vehicleDotIconUrl,
  iconSize: [24, 24],
  iconAnchor: [12, 16],
  // Leaflet only accepts pixel offsets
  tooltipAnchor: [0, -16],
});
